import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Loader2, MessageCircle, Compass, AlertTriangle, Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Customer Intake Summary — verbatim recap surface backed by the
 * Expectation Management Engine.
 *
 *   Two modes:
 *     mode="live"      — fetch /api/intake/sessions/{id}/expectations
 *                        (used on the workspace + post-submit Review)
 *     mode="preview"   — render the supplied `expectations` payload directly
 *                        (used inside the Review stage where we already have it)
 *
 * Sections:
 *   1. Service summary headline (what they're in for)
 *   2. Recap sentences — "say to the customer" verbatim (read aloud)
 *   3. Retention steers — calm framing layer
 *   4. Watch-outs (FOH-only, never read aloud)
 *
 * The FOH gets a one-click "Copy all" button so they can paste the recap
 * into Garage Hive / WhatsApp / email if the customer asks for it in writing.
 */
export default function CustomerIntakeSummary({
  sessionId,
  mode = "live",
  expectations: providedExpectations = null,
  testIdPrefix = "intake-summary",
  showWatchOuts = true,
  className = "",
}) {
  const [data, setData] = useState(providedExpectations);
  const [loading, setLoading] = useState(mode === "live" && !providedExpectations);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (mode !== "live" || !sessionId || providedExpectations) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/intake/sessions/${sessionId}/expectations`
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.data?.detail || "Could not load intake summary"
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, mode, providedExpectations]);

  const handleCopy = async () => {
    if (!data) return;
    const lines = [
      data.service_summary || "Visit booked",
      "",
      ...(data.recap_sentences || []),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* silent */
    }
  };

  const handleEmail = async () => {
    if (!sessionId || emailing) return;
    setEmailing(true);
    try {
      const r = await axios.post(
        `${API}/intake/sessions/${sessionId}/email-recap`
      );
      setEmailSent(true);
      toast.success(`Recap emailed to ${r.data?.email || "the customer"}.`);
      setTimeout(() => setEmailSent(false), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send recap email");
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`rounded-lg border border-slate-200 bg-white p-4 ${className}`}
        data-testid={`${testIdPrefix}-loading`}
      >
        <p className="text-[14px] text-slate-500 inline-flex items-center">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Loading customer recap…
        </p>
      </div>
    );
  }
  if (error) {
    // In `live` mode the recap is a workshop-side enhancement, not critical
    // intel — render nothing rather than a scary red error box. `preview`
    // mode still surfaces the error because the FOH is actively waiting on
    // it inside the Review stage.
    if (mode === "live") return null;
    return (
      <div
        className={`rounded-lg border border-rose-200 bg-rose-50 p-4 ${className}`}
        data-testid={`${testIdPrefix}-error`}
      >
        <p className="text-[14px] text-rose-800">{error}</p>
      </div>
    );
  }
  if (!data) return null;

  const recap = data.recap_sentences || [];
  const steers = data.retention_steers || [];
  const watchOuts = data.watch_outs || [];

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white overflow-hidden ${className}`}
      data-testid={testIdPrefix}
    >
      <header className="px-4 py-3 bg-gradient-to-r from-slate-900 via-slate-800 to-teal-800 text-white flex items-center justify-between gap-3">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold opacity-80">
            Customer intake summary
          </p>
          <h3 className="text-[17px] font-bold leading-tight">
            {data.service_summary || "Visit booked"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {sessionId && mode === "live" && (
            <button
              type="button"
              onClick={handleEmail}
              disabled={emailing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-semibold bg-white/15 hover:bg-white/25 disabled:opacity-50 transition-colors"
              data-testid={`${testIdPrefix}-email`}
              title="Email recap to the customer"
            >
              {emailing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : emailSent ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Mail className="w-3.5 h-3.5" />
              )}
              {emailing ? "Sending" : emailSent ? "Sent" : "Email"}
            </button>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-semibold bg-white/15 hover:bg-white/25 transition-colors"
            data-testid={`${testIdPrefix}-copy`}
            title="Copy recap to clipboard"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </header>

      <div className="px-4 py-3.5 space-y-4">
        {recap.length > 0 && (
          <Section
            icon={<MessageCircle className="w-4 h-4" />}
            label="Read aloud to the customer"
            testId={`${testIdPrefix}-recap`}
          >
            <ol className="space-y-2.5">
              {recap.map((line, i) => (
                <li
                  key={i}
                  className="text-[15px] text-slate-900 leading-relaxed italic pl-3 border-l-2 border-teal-300"
                  data-testid={`${testIdPrefix}-recap-line-${i}`}
                >
                  &ldquo;{line}&rdquo;
                </li>
              ))}
            </ol>
          </Section>
        )}

        {steers.length > 0 && (
          <Section
            icon={<Compass className="w-4 h-4" />}
            label="If they push back"
            testId={`${testIdPrefix}-steers`}
          >
            <ul className="space-y-2">
              {steers.map((line, i) => (
                <li
                  key={i}
                  className="text-[14px] text-slate-700 leading-relaxed"
                  data-testid={`${testIdPrefix}-steer-line-${i}`}
                >
                  {line}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {showWatchOuts && watchOuts.length > 0 && (
          <Section
            icon={<AlertTriangle className="w-4 h-4 text-rose-600" />}
            label="FOH-only watch-outs"
            testId={`${testIdPrefix}-watch-outs`}
            tone="rose"
          >
            <ul className="space-y-2">
              {watchOuts.map((w, i) => (
                <li
                  key={i}
                  className="text-[14px] text-rose-900 leading-relaxed bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                  data-testid={`${testIdPrefix}-watch-line-${i}`}
                >
                  {w}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ icon, label, testId, tone = "slate", children }) {
  const labelTone =
    tone === "rose" ? "text-rose-700" : "text-slate-600";
  return (
    <section data-testid={testId}>
      <div
        className={`flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.18em] font-semibold mb-2 ${labelTone}`}
      >
        {icon}
        {label}
      </div>
      {children}
    </section>
  );
}
